import CustomDatePicker from '@/components/shared/CustomDatePicker';
import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, ArrowUpDown, PackageCheck, CheckCircle2, RotateCcw, ExternalLink, Undo2, Trash2, Package, Pencil, Play, Clock, Truck, Zap, FlaskConical, Layers } from 'lucide-react';
import { useSession } from '@/lib/SessionContext';
import { useAsaConfirm } from '@/contexts/AsaConfirmContext';
import { canManagePO } from '@/lib/AuthSession';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import BrandCard from '@/components/production/BrandCard';
import ShippingCard from '@/components/production/ShippingCard';
import AddOrderDialog from '@/components/production/AddOrderDialog';
import AsaGreetingWidget from '@/components/AsaGreetingWidget';
import LastUpdated from '@/components/ui/LastUpdated';
import SearchableSelect from '@/components/ui/SearchableSelect';
import { deductStockForPO } from '@/lib/stockDeduction';

// ── Return constants ──────────────────────────────────────────────
const REASONS = [
  { value: 'cacat_produksi', label: 'Cacat Produksi', color: 'bg-red-100 text-red-700' },
  { value: 'salah_kirim', label: 'Salah Kirim', color: 'bg-orange-100 text-orange-700' },
  { value: 'kadaluarsa', label: 'Kadaluarsa', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'permintaan_customer', label: 'Permintaan Customer', color: 'bg-blue-100 text-blue-700' },
  { value: 'lainnya', label: 'Lainnya', color: 'bg-gray-100 text-gray-700' },
];
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600' },
  diterima: { label: 'Diterima', color: 'bg-blue-100 text-blue-700' },
  diproses: { label: 'Diproses', color: 'bg-yellow-100 text-yellow-700' },
  selesai: { label: 'Selesai', color: 'bg-green-100 text-green-700' },
  ditolak: { label: 'Ditolak', color: 'bg-red-100 text-red-700' },
};
const ACTION_CONFIG = {
  rework: { label: 'Rework', color: 'bg-purple-100 text-purple-700' },
  dispose: { label: 'Dispose', color: 'bg-red-100 text-red-700' },
  restock: { label: 'Restock', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Belum Ditentukan', color: 'bg-gray-100 text-gray-600' },
};
const REASON_OPTIONS = REASONS.map(r => ({ value: r.value, label: r.label }));
const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([v, c]) => ({ value: v, label: c.label }));
const ACTION_OPTIONS = [
  { value: 'rework', label: 'Rework' },
  { value: 'dispose', label: 'Dispose' },
  { value: 'restock', label: 'Restock' },
  { value: 'pending', label: 'Belum Ditentukan' },
];

function ReturnDialog({ item, orders, onSave, trigger }) {
  const isEdit = !!item;
  const today = new Date().toISOString().split('T')[0];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(
    item ? {
      return_number: item.return_number || '',
      brand_name: item.brand_name,
      product_name: item.product_name || '',
      return_date: item.return_date,
      qty_returned: item.qty_returned,
      reason: item.reason,
      return_status: item.return_status,
      action_taken: item.action_taken || 'pending',
      notes: item.notes || '',
    } : {
      return_number: '',
      brand_name: '',
      product_name: '',
      return_date: today,
      qty_returned: '',
      reason: 'cacat_produksi',
      return_status: 'pending',
      action_taken: 'pending',
      notes: '',
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave({ ...form, qty_returned: Number(form.qty_returned) || 0 });
    setOpen(false);
  };

  const brandOptions = [...new Set(orders.map(o => o.brand_name).filter(Boolean))].map(b => ({ value: b, label: b }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? 'Edit Return' : 'Catat Return Baru'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>No. Return</Label><Input value={form.return_number} onChange={(e) => setForm(f => ({...f, return_number: e.target.value}))} placeholder="RTN-001" /></div>
            <div><Label>Tanggal</Label><CustomDatePicker value={form.return_date} onChange={(val) => setForm(f => ({...f, return_date: val}))} required /></div>
          </div>
          <div>
            <Label>Brand</Label>
            {brandOptions.length > 0 ? (
              <SearchableSelect value={form.brand_name} onValueChange={v => setForm(f => ({ ...f, brand_name: v }))} options={[...brandOptions, { value: '__other', label: 'Lainnya (ketik manual)' }]} placeholder="Pilih brand..." />
            ) : (
              <Input value={form.brand_name} onChange={(e) => setForm(f => ({...f, brand_name: e.target.value}))} required />
            )}
            {form.brand_name === '__other' && <Input className="mt-1" placeholder="Nama brand" onChange={(e) => setForm(f => ({...f, brand_name: e.target.value}))} />}
          </div>
          <div><Label>Nama Produk</Label><Input value={form.product_name} onChange={(e) => setForm(f => ({...f, product_name: e.target.value}))} placeholder="Opsional" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Qty Return</Label><Input type="number" value={form.qty_returned} onChange={(e) => setForm(f => ({...f, qty_returned: e.target.value}))} required /></div>
            <div><Label>Alasan</Label><SearchableSelect value={form.reason} onValueChange={v => setForm(f => ({ ...f, reason: v }))} options={REASON_OPTIONS} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Status</Label><SearchableSelect value={form.return_status} onValueChange={v => setForm(f => ({ ...f, return_status: v }))} options={STATUS_OPTIONS} /></div>
            <div><Label>Tindakan</Label><SearchableSelect value={form.action_taken} onValueChange={v => setForm(f => ({ ...f, action_taken: v }))} options={ACTION_OPTIONS} /></div>
          </div>
          <div><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm(f => ({...f, notes: e.target.value}))} /></div>
          <Button type="submit" className="w-full">{isEdit ? 'Simpan Perubahan' : 'Catat Return'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { member } = useSession();
  const { confirm } = useAsaConfirm();
  const canManage = canManagePO(member);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('order_number');
  const [activeTab, setActiveTab] = useState('pending');
  const [inProgressSubFilter, setInProgressSubFilter] = useState('all'); // 'all' | 'primer' | 'sekunder'
  const [shippedSearch, setShippedSearch] = useState('');
  const [shippedSort, setShippedSort] = useState('shipped_at');
  const [shippedTypeFilter, setShippedTypeFilter] = useState('all');
  const queryClient = useQueryClient();

  const isDirectCompletion = (order) => {
    if (!order) return false;
    if (order.diselesaikan_langsung === true || order.completion_type === 'direct') return true;
    if ((order.status === 'done' || order.is_shipped) && (!order.task_stock_deducted || (!order.task_stock_deducted.racik && !order.task_stock_deducted.filling))) {
      return true;
    }
    return false;
  };

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['production-orders'],
    queryFn: () => base44.entities.ProductionOrder.list('order_number'),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const { data: rawInventory = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => base44.entities.InventoryItem.list('item_name'),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });

  const inventoryItems = useMemo(() => {
    const fromStok = [
      ...botolList.map(i => ({ ...i, id: `botol-${i.id}`, _stokId: i.id, _entity: 'Botol', total_stock: i.stok ?? 0, item_name: i.nama })),
      ...tutupList.map(i => ({ ...i, id: `tutup-${i.id}`, _stokId: i.id, _entity: 'Tutup', total_stock: i.stok ?? 0, item_name: i.nama })),
      ...sprayList.map(i => ({ ...i, id: `spray-${i.id}`, _stokId: i.id, _entity: 'Spray', total_stock: i.stok ?? 0, item_name: i.nama })),
      ...bahanList.map(i => ({ ...i, id: `bahan-${i.id}`, _stokId: i.id, _entity: 'BahanCair', total_stock: i.stok ?? 0, item_name: i.nama })),
      ...itemTambahanList.map(i => ({ ...i, id: `item-${i.id}`, _stokId: i.id, _entity: 'ItemTambahan', total_stock: i.stok ?? 0, item_name: i.nama })),
      ...fullsetList.map(i => ({ ...i, id: `fullset-${i.id}`, _stokId: i.id, _entity: 'Fullset', total_stock: i.stok ?? 0, item_name: i.nama })),
    ];
    const combinedNames = new Set(fromStok.map(i => i.item_name?.toLowerCase()));
    const onlyRaw = rawInventory.filter(i => !combinedNames.has(i.item_name?.toLowerCase()));
    return [...fromStok, ...onlyRaw];
  }, [rawInventory, botolList, tutupList, sprayList, bahanList, itemTambahanList, fullsetList]);

  // ── Selesai (shipped) ──
  const { data: shippedOrders = [], isLoading: shippedLoading } = useQuery({
    queryKey: ['shipped-orders'],
    queryFn: () => base44.entities.ProductionOrder.filter({ is_shipped: true }),
  });

  const deleteMutationShipped = useMutation({
    mutationFn: (id) => base44.entities.ProductionOrder.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      toast.success('PO dihapus');
    },
  });

  const unshipMutation = useMutation({
    mutationFn: (id) => base44.entities.ProductionOrder.update(id, { is_shipped: false, shipped_at: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      toast.success('PO dikembalikan ke pengiriman');
    },
  });

  const filteredShipped = shippedOrders
    .filter(o => o.status === 'done' || o.is_shipped)
    .filter(o => {
      if (shippedTypeFilter === 'direct') return isDirectCompletion(o);
      if (shippedTypeFilter === 'standard') return !isDirectCompletion(o);
      return true;
    })
    .filter(o => !shippedSearch || o.brand_name?.toLowerCase().includes(shippedSearch.toLowerCase()) || o.product_name?.toLowerCase().includes(shippedSearch.toLowerCase()))
    .sort((a, b) => {
      const getExactTime = (o) => {
        const d1 = o.tanggal_selesai;
        const d2 = o.completed_at;
        const d3 = o.updated_date;
        const d4 = o.shipped_at;
        const d5 = o.created_date;
        if (d1 && String(d1).length > 10) return new Date(d1).getTime();
        if (d2 && String(d2).length > 10) return new Date(d2).getTime();
        if (d3 && String(d3).length > 10) return new Date(d3).getTime();
        if (d4 && String(d4).length > 10) return new Date(d4).getTime();
        if (d5 && String(d5).length > 10) return new Date(d5).getTime();
        if (d4) return new Date(d4).getTime();
        if (d1) return new Date(d1).getTime();
        if (d3) return new Date(d3).getTime();
        return 0;
      };

      if (shippedSort === 'shipped_at' || shippedSort === 'newest') {
        return getExactTime(b) - getExactTime(a);
      }
      if (shippedSort === 'oldest') {
        return getExactTime(a) - getExactTime(b);
      }
      if (shippedSort === 'brand_name') return (a.brand_name || '').localeCompare(b.brand_name || '');
      if (shippedSort === 'brand_name_desc') return (b.brand_name || '').localeCompare(a.brand_name || '');
      if (shippedSort === 'order_number_desc') return (Number(b.order_number) || 0) - (Number(a.order_number) || 0);
      if (shippedSort === 'order_number_asc') return (Number(a.order_number) || 0) - (Number(b.order_number) || 0);
      return getExactTime(b) - getExactTime(a);
    });

  // ── Return ──
  const { data: returns = [], isLoading: returnsLoading } = useQuery({
    queryKey: ['returns'],
    queryFn: () => base44.entities.ReturnOrder.list('-return_date'),
  });

  const createReturnMutation = useMutation({
    mutationFn: (data) => base44.entities.ReturnOrder.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['returns'] }); toast.success('Return dicatat'); },
  });

  const updateReturnMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.ReturnOrder.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['returns'] }); toast.success('Return diperbarui'); },
  });

  const deleteReturnMutation = useMutation({
    mutationFn: (id) => base44.entities.ReturnOrder.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['returns'] }); toast.success('Return dihapus'); },
  });

  const returnStats = {
    total: returns.length,
    pending: returns.filter(r => r.return_status === 'pending').length,
    diproses: returns.filter(r => r.return_status === 'diproses').length,
    selesai: returns.filter(r => r.return_status === 'selesai').length,
    totalQty: returns.reduce((s, r) => s + (r.qty_returned || 0), 0),
  };

  // Buat stockMap dari inventori terbaru (ID, _stokId, dan nama)
  const stockMap = useMemo(() => {
    const map = {};
    inventoryItems.forEach(item => {
      if (item.id) map[item.id] = item.total_stock || 0;
      if (item._stokId) map[item._stokId] = item.total_stock || 0;
      if (item.item_name) map[item.item_name.toLowerCase().trim()] = item.total_stock || 0;
    });
    return map;
  }, [inventoryItems]);

  // Inject status real-time ke setiap komponen PO & pastikan tidak ada data ganda (deduplication)
  const ordersWithRealtimeStatus = useMemo(() => {
    const seen = new Set();
    const unique = [];
    orders.forEach(o => {
      const numKey = o.order_number ? String(o.order_number).trim().toLowerCase() : null;
      const idKey = o.id ? String(o.id).trim().toLowerCase() : null;
      
      if (numKey && seen.has(numKey)) return;
      if (idKey && seen.has(idKey)) return;

      if (numKey) seen.add(numKey);
      if (idKey) seen.add(idKey);
      unique.push(o);
    });
    return unique;
  }, [orders]);

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.ProductionOrder.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['production-orders'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.ProductionOrder.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['production-orders'] }),
  });

  const PENDING_STATUSES = ['pending', 'sedang_po_item', 'antrian_produksi'];
  // Tahap Primer: Mixing / Racik, Maserasi, Filling, QC Primer
  const PRIMER_STATUSES = ['in_progress', 'diracik', 'menunggu_qc_mixing', 'maserasi', 'menunggu_filling', 'filling', 'menunggu_qc_filling'];
  // Tahap Sekunder: Pengemasan, Labeling, Dus Satuan, Capping, Sealing, QC Pengemasan
  const SEKUNDER_STATUSES = ['menunggu_packing', 'packing', 'waiting_filling', 'menunggu_qc_packing'];
  const IN_PROGRESS_STATUSES = [...PRIMER_STATUSES, ...SEKUNDER_STATUSES];
  const SIAP_KIRIM_STATUSES = ['siap_kirim'];
  const PENGIRIMAN_STATUSES = ['dalam_pengiriman', 'shipping'];

  const activeOrders = ordersWithRealtimeStatus.filter(o => o.status !== 'done');
  const pendingOrders = activeOrders.filter(o => PENDING_STATUSES.includes(o.status));
  const inProgressOrders = activeOrders.filter(o => IN_PROGRESS_STATUSES.includes(o.status));
  const primerOrders = inProgressOrders.filter(o => PRIMER_STATUSES.includes(o.status));
  const sekunderOrders = inProgressOrders.filter(o => SEKUNDER_STATUSES.includes(o.status));
  const siapKirimOrders = activeOrders.filter(o => SIAP_KIRIM_STATUSES.includes(o.status));
  const pengirimanOrders = activeOrders.filter(o => PENGIRIMAN_STATUSES.includes(o.status));

  const stats = {
    total: activeOrders.length,
    pending: pendingOrders.length,
    inProgress: inProgressOrders.length,
    primer: primerOrders.length,
    sekunder: sekunderOrders.length,
    siapKirim: siapKirimOrders.length,
    pengiriman: pengirimanOrders.length,
  };

  const applyFilter = (list) =>
    list
      .filter(o => !search || o.brand_name?.toLowerCase().includes(search.toLowerCase()) || o.product_name?.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        if (sortBy === 'newest') {
          const tB = new Date(b.created_date || b.updated_date || 0).getTime();
          const tA = new Date(a.created_date || a.updated_date || 0).getTime();
          return tB - tA;
        }
        if (sortBy === 'oldest') {
          const tB = new Date(b.created_date || b.updated_date || 0).getTime();
          const tA = new Date(a.created_date || a.updated_date || 0).getTime();
          return tA - tB;
        }
        if (sortBy === 'order_number_desc') return (Number(b.order_number) || 0) - (Number(a.order_number) || 0);
        if (sortBy === 'order_number') return (Number(a.order_number) || 0) - (Number(b.order_number) || 0);
        if (sortBy === 'brand_name') return (a.brand_name || '').localeCompare(b.brand_name || '');
        if (sortBy === 'brand_name_desc') return (b.brand_name || '').localeCompare(a.brand_name || '');
        if (sortBy === 'deadline') return new Date(a.deadline || '2099-12-31') - new Date(b.deadline || '2099-12-31');
        if (sortBy === 'progress') {
          const getP = (o) => {
            const comps = o.components || [];
            return comps.length > 0 ? comps.filter(c => c.status === 'ready').length / comps.length : 0;
          };
          return getP(b) - getP(a);
        }
        return 0;
      });

  return (
    <div className="space-y-6">
      <AsaGreetingWidget />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div className="bg-white/40 p-6 rounded-[2rem] border border-white/50 shadow-sm flex-1">
          <h1 className="font-display text-4xl font-black tracking-tight text-slate-800">Active Production</h1>
          <p className="text-slate-500 font-medium mt-2">
            Pantau dan kelola semua proses produksi di satu tempat.
          </p>
        </div>
        {canManage && (
          <AddOrderDialog
            onSave={(data) => createMutation.mutateAsync(data)}
            trigger={
              <Button className="gap-2 h-14 px-8 rounded-2xl bg-gradient-to-r from-[#8C6208] to-[#6a4a06] hover:from-[#6a4a06] hover:to-[#4a3404] text-white shadow-lg hover:shadow-xl transition-all duration-300">
                <Plus className="w-5 h-5" /> Tambah PO
              </Button>
            }
          />
        )}
      </div>

      {/* Stats pills */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Pending', value: stats.pending, sub: 'Menunggu Bahan', color: 'from-slate-100 to-slate-50 text-slate-700 border-slate-200/60', icon: Clock },
          { label: 'Berjalan', value: stats.inProgress, sub: `${stats.primer} Primer · ${stats.sekunder} Sekunder`, color: 'from-[#F9F5EC] to-white text-[#8C6208] border-[#E6D5B8]', icon: Play },
          { label: 'Siap Kirim', value: stats.siapKirim, sub: 'Gudang Barang Jadi', color: 'from-teal-50/80 to-white text-teal-800 border-teal-200/60', icon: PackageCheck },
          { label: 'Pengiriman', value: stats.pengiriman, sub: 'Dalam Perjalanan', color: 'from-blue-50/80 to-white text-blue-700 border-blue-100', icon: Truck },
        ].map((s) => {
          const IconComp = s.icon;
          return (
            <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-2xl md:rounded-[2rem] px-6 py-5 border shadow-sm flex items-center justify-between group hover:shadow-md transition-all duration-300`}>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider opacity-80 mb-1">{s.label}</div>
                <div className="text-3xl md:text-4xl font-black tracking-tight">{s.value}</div>
                {s.sub && <div className="text-[11px] font-semibold opacity-80 mt-1">{s.sub}</div>}
              </div>
              <div className="w-12 h-12 rounded-full bg-white/60 flex items-center justify-center shadow-sm">
                <IconComp className="w-6 h-6 opacity-70" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cari brand..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-56">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">PO Terbaru</SelectItem>
            <SelectItem value="order_number_desc">No. Urut (Terbesar / Baru)</SelectItem>
            <SelectItem value="order_number">No. Urut (Terkecil)</SelectItem>
            <SelectItem value="brand_name">Nama Brand (A-Z)</SelectItem>
            <SelectItem value="brand_name_desc">Nama Brand (Z-A)</SelectItem>
            <SelectItem value="deadline">Deadline Terdekat</SelectItem>
            <SelectItem value="progress">Progress Tertinggi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs: Pending, Berjalan, Siap Kirim, Pengiriman, Selesai */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="bg-white/60 p-2 rounded-2xl border border-white/40 shadow-sm inline-block">
          <TabsList className="flex-wrap h-auto gap-1 bg-transparent p-0">
            <TabsTrigger value="pending" className="gap-2 rounded-xl px-5 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Clock className="w-4 h-4" />
              Pending ({pendingOrders.length})
            </TabsTrigger>
            <TabsTrigger value="inProgress" className="gap-2 rounded-xl px-5 py-2.5 data-[state=active]:bg-white data-[state=active]:text-[#8C6208] data-[state=active]:shadow-sm">
              <Play className="w-4 h-4" />
              Berjalan ({inProgressOrders.length})
            </TabsTrigger>
            <TabsTrigger value="siapKirim" className="gap-2 rounded-xl px-5 py-2.5 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm">
              <PackageCheck className="w-4 h-4" />
              Siap Kirim ({siapKirimOrders.length})
            </TabsTrigger>
            <TabsTrigger value="pengiriman" className="gap-2 rounded-xl px-5 py-2.5 data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm">
              <Truck className="w-4 h-4" />
              Pengiriman ({pengirimanOrders.length})
            </TabsTrigger>
            <TabsTrigger value="shipped" className="gap-2 rounded-xl px-5 py-2.5 data-[state=active]:bg-white data-[state=active]:text-green-600 data-[state=active]:shadow-sm">
              <CheckCircle2 className="w-4 h-4" />
              Selesai
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Pending */}
        <TabsContent value="pending" className="mt-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(3).fill(0).map((_, i) => <div key={i} className="h-40 rounded-xl bg-muted animate-pulse" />)}
            </div>
          ) : applyFilter(pendingOrders).length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Belum ada PO yang pending</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {applyFilter(pendingOrders).map(order => (
                <BrandCard key={order.id} order={order} stockMap={stockMap} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Berjalan */}
        <TabsContent value="inProgress" className="mt-4 space-y-4">
          {/* Sub-Filter: Clean Minimalist Segmented Pill */}
          <div className="inline-flex items-center gap-1 p-1 bg-stone-100/90 rounded-xl border border-stone-200/60">
            <button
              type="button"
              onClick={() => setInProgressSubFilter('all')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                inProgressSubFilter === 'all'
                  ? 'bg-white text-[#8C6208] shadow-xs font-bold'
                  : 'text-stone-500 hover:text-stone-800'
              }`}
            >
              Semua ({inProgressOrders.length})
            </button>
            <button
              type="button"
              onClick={() => setInProgressSubFilter('primer')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                inProgressSubFilter === 'primer'
                  ? 'bg-white text-[#8C6208] shadow-xs font-bold'
                  : 'text-stone-500 hover:text-stone-800'
              }`}
            >
              Tahap Primer ({primerOrders.length})
            </button>
            <button
              type="button"
              onClick={() => setInProgressSubFilter('sekunder')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                inProgressSubFilter === 'sekunder'
                  ? 'bg-white text-[#8C6208] shadow-xs font-bold'
                  : 'text-stone-500 hover:text-stone-800'
              }`}
            >
              Tahap Sekunder ({sekunderOrders.length})
            </button>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(3).fill(0).map((_, i) => <div key={i} className="h-40 rounded-xl bg-muted animate-pulse" />)}
            </div>
          ) : applyFilter(
              inProgressSubFilter === 'primer' 
                ? primerOrders 
                : inProgressSubFilter === 'sekunder' 
                  ? sekunderOrders 
                  : inProgressOrders
            ).length === 0 ? (
            <div className="text-center py-20 text-muted-foreground bg-white/40 rounded-2xl border border-dashed border-stone-200">
              <Play className="w-12 h-12 mx-auto mb-3 opacity-30 text-stone-400" />
              <p>
                Belum ada PO di {inProgressSubFilter === 'primer' ? 'Tahap Primer' : inProgressSubFilter === 'sekunder' ? 'Tahap Sekunder' : 'proses berjalan'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {applyFilter(
                inProgressSubFilter === 'primer' 
                  ? primerOrders 
                  : inProgressSubFilter === 'sekunder' 
                    ? sekunderOrders 
                    : inProgressOrders
              ).map(order => (
                <BrandCard key={order.id} order={order} stockMap={stockMap} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Siap Kirim (Barang Jadi / Holding Area Gudang) */}
        <TabsContent value="siapKirim" className="mt-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(3).fill(0).map((_, i) => <div key={i} className="h-40 rounded-xl bg-muted animate-pulse" />)}
            </div>
          ) : applyFilter(siapKirimOrders).length === 0 ? (
            <div className="text-center py-20 text-muted-foreground bg-white/40 rounded-2xl border border-dashed border-slate-200">
              <PackageCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Belum ada PO dalam status Siap Kirim (Kemasan tuntas & menunggu ekspedisi)</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {applyFilter(siapKirimOrders).map(order => (
                <ShippingCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Pengiriman (In Transit) */}
        <TabsContent value="pengiriman" className="mt-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(3).fill(0).map((_, i) => <div key={i} className="h-40 rounded-xl bg-muted animate-pulse" />)}
            </div>
          ) : applyFilter(pengirimanOrders).length === 0 ? (
            <div className="text-center py-20 text-muted-foreground bg-white/40 rounded-2xl border border-dashed border-slate-200">
              <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Belum ada PO dalam status perjalanan pengiriman</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {applyFilter(pengirimanOrders).map(order => (
                <ShippingCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Selesai (Arsip) */}
        <TabsContent value="shipped" className="mt-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Cari brand atau produk..." value={shippedSearch} onChange={(e) => setShippedSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={shippedTypeFilter} onValueChange={setShippedTypeFilter}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Tipe Selesai" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Tipe Selesai</SelectItem>
                <SelectItem value="direct">Diselesaikan Langsung</SelectItem>
                <SelectItem value="standard">Alur Produksi Penuh</SelectItem>
              </SelectContent>
            </Select>
            <Select value={shippedSort} onValueChange={setShippedSort}>
              <SelectTrigger className="w-56">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shipped_at">Paling Baru Selesai (Terbaru)</SelectItem>
                <SelectItem value="oldest">Paling Lama Selesai (Terlama)</SelectItem>
                <SelectItem value="brand_name">Nama Brand (A-Z)</SelectItem>
                <SelectItem value="brand_name_desc">Nama Brand (Z-A)</SelectItem>
                <SelectItem value="order_number_desc">No. PO (Terbesar)</SelectItem>
                <SelectItem value="order_number_asc">No. PO (Terkecil)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {shippedLoading ? (
            <div className="space-y-3">{Array(4).fill(0).map((_, i) => <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />)}</div>
          ) : filteredShipped.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Belum ada PO yang selesai dikirim</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredShipped.map(order => (
                <Card key={order.id} className="border-green-200/40 bg-green-50/20 group hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-base truncate">{order.brand_name}</h3>
                          {isDirectCompletion(order) && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-300">
                              <Zap className="w-2.5 h-2.5 text-amber-700" /> Selesai Langsung
                            </span>
                          )}
                        </div>
                        {order.product_name && <p className="text-xs text-muted-foreground truncate">{order.product_name}</p>}
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {(() => {
                            const doneDate = order.shipped_at || order.tanggal_selesai || order.completed_at || order.updated_date || order.created_date;
                            if (!doneDate) return null;
                            return (
                              <p className="text-xs font-medium text-green-700">
                                {new Date(doneDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </p>
                            );
                          })()}
                          {(() => {
                            const displayQty = order.final_qty || order.target_qty || (order.products || []).reduce((s, p) => s + (Number(p.quantity || p.target_qty) || 0), 0) || 0;
                            if (displayQty <= 0) return null;
                            return (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Package className="w-3 h-3" />{displayQty.toLocaleString()} pcs
                              </p>
                            );
                          })()}
                          {order.courier && <p className="text-xs text-muted-foreground">{order.courier}{order.tracking_number ? ` · ${order.tracking_number}` : ''}</p>}
                          <LastUpdated date={order.updated_date || order.created_date} />
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link to={`/office/production/${order.id}`} title="Lihat detail PO">
                          <Button variant="ghost" size="icon" className="h-8 w-8"><ExternalLink className="w-4 h-4" /></Button>
                        </Link>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-yellow-600 hover:text-yellow-700" title="Kembalikan ke pengiriman"
                          onClick={async () => { if (await confirm('Kembalikan PO ini ke tab Pengiriman?')) unshipMutation.mutate(order.id); }}>
                          <Undo2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Hapus PO"
                          onClick={async () => { if (await confirm(`Hapus PO "${order.brand_name}" secara permanen?`)) deleteMutationShipped.mutate(order.id); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Return */}
        <TabsContent value="return" className="mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
              <div className="bg-secondary rounded-xl px-4 py-3 border border-border/40">
                <div className="text-2xl font-bold">{returnStats.total}</div><div className="text-xs text-muted-foreground">Total Return</div>
              </div>
              <div className="bg-gray-50 rounded-xl px-4 py-3 border border-border/40">
                <div className="text-2xl font-bold">{returnStats.pending}</div><div className="text-xs text-muted-foreground">Pending</div>
              </div>
              <div className="bg-yellow-50 rounded-xl px-4 py-3 border border-yellow-200/50">
                <div className="text-2xl font-bold text-yellow-700">{returnStats.diproses}</div><div className="text-xs text-yellow-600">Diproses</div>
              </div>
              <div className="bg-green-50 rounded-xl px-4 py-3 border border-green-200/50">
                <div className="text-2xl font-bold text-green-700">{returnStats.selesai}</div><div className="text-xs text-green-600">Selesai</div>
              </div>
            </div>
            <ReturnDialog
              orders={orders}
              onSave={(data) => createReturnMutation.mutateAsync(data)}
              trigger={<Button className="gap-2 shrink-0"><Plus className="w-4 h-4" /> Catat Return</Button>}
            />
          </div>
          {returnsLoading ? (
            <div className="space-y-3">{Array(4).fill(0).map((_, i) => <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />)}</div>
          ) : returns.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <RotateCcw className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Belum ada data return</p>
            </div>
          ) : (
            <div className="space-y-3">
              {returns.map(ret => {
                const reason = REASONS.find(r => r.value === ret.reason);
                const status = STATUS_CONFIG[ret.return_status] || STATUS_CONFIG.pending;
                const action = ACTION_CONFIG[ret.action_taken] || ACTION_CONFIG.pending;
                return (
                  <Card key={ret.id} className="group hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {ret.return_number && <span className="text-xs font-mono text-muted-foreground">{ret.return_number}</span>}
                            <h3 className="font-semibold">{ret.brand_name}</h3>
                            {ret.product_name && <span className="text-xs text-muted-foreground">· {ret.product_name}</span>}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${status.color}`}>{status.label}</span>
                            {reason && <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${reason.color}`}>{reason.label}</span>}
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${action.color}`}>{action.label}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm flex-wrap">
                            <span className="text-destructive font-medium">{(ret.qty_returned || 0).toLocaleString()} unit</span>
                            {ret.return_date && <span className="text-xs text-muted-foreground">{new Date(ret.return_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}

                          </div>
                          {ret.notes && <p className="text-xs text-muted-foreground mt-1 italic">{ret.notes}</p>}
                          <div className="mt-1.5"><LastUpdated date={ret.updated_date || ret.created_date} /></div>
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                          {ret.return_status === 'pending' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateReturnMutation.mutate({ id: ret.id, data: { return_status: 'diterima' } })}>Terima</Button>}
                          {ret.return_status === 'diterima' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateReturnMutation.mutate({ id: ret.id, data: { return_status: 'diproses' } })}>Proses</Button>}
                          {ret.return_status === 'diproses' && <Button size="sm" variant="outline" className="h-7 text-xs text-green-700 border-green-300" onClick={() => updateReturnMutation.mutate({ id: ret.id, data: { return_status: 'selesai' } })}>Selesai</Button>}
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ReturnDialog item={ret} orders={orders} onSave={(data) => updateReturnMutation.mutateAsync({ id: ret.id, data })} trigger={<Button variant="ghost" size="icon" className="h-7 w-7"><Pencil className="w-3 h-3" /></Button>} />
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={async () => { if (await confirm('Hapus data return ini?')) deleteReturnMutation.mutate(ret.id); }}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}